import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/Auth/authContext';
import { useClientAccess } from '../../../hooks/useClientAccess';
import { useI18n } from '../../../contexts/I18nContext';
import { apiClient } from '../../../services/apiClient';
import { DeviceUpdateStatus, DeviceVersionItem, FirmwareItem, OtaStats, OtaUpdate, Paginated } from '../../../types/otaUpdate';
import { buildDeviceStatus } from './otaData';

export type FirmwareActiveFilter = 'all' | 'active' | 'inactive';

/**
 * The OTA screen's data: one server-side page of firmware (model, type and
 * inactive flag applied by the API; search and status applied here), the
 * fleet figures, and the gateways' reported versions.
 */
export const useOtaOverview = (refreshTrigger?: number) => {
  const { isAuthenticated } = useAuth();
  const { isAdmin, isSuperAdmin } = useClientAccess();
  const canSeeInactive = isAdmin || isSuperAdmin;
  const { t } = useI18n();
  const axios = useMemo(() => apiClient.getAxiosInstance(), []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localRefresh, setLocalRefresh] = useState(0);

  const [firmwareItems, setFirmwareItems] = useState<FirmwareItem[]>([]);
  const [deviceStatus, setDeviceStatus] = useState<DeviceUpdateStatus[]>([]);
  // Scheduled updates are not exposed by the API yet: the tab stays empty.
  const [pendingUpdates] = useState<OtaUpdate[]>([]);
  const [stats, setStats] = useState<OtaStats | null>(null);

  // Firmware filters. Model, type and the inactive flag go to the server; search and status narrow the page.
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [model, setModel] = useState('');
  const [type, setType] = useState('');
  const [active, setActive] = useState<FirmwareActiveFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [model, type, showInactive]);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const params: Record<string, any> = { page, limit: pageSize };
        if (showInactive && canSeeInactive) params.include_inactive = true;
        if (model) params.model = model;
        if (type) params.type = type;
        const firmware = await axios.get<Paginated<FirmwareItem>>('/v2/firmware', { params });
        if (cancelled) return;
        setFirmwareItems(firmware.data.items);
        setTotal(firmware.data.total ?? firmware.data.items.length);
        setTotalPages(firmware.data.pages ?? 1);

        // The figures and the gateways' versions are best effort: the screen works without them.
        try {
          const res = await axios.get<OtaStats>('/v2/firmware/stats');
          if (!cancelled) setStats(res.data);
        } catch (statsError) {
          console.warn('[OTA UPDATES] Could not fetch stats:', statsError);
        }
        try {
          const res = await axios.get<Paginated<DeviceVersionItem>>('/v2/device-versions', { params: { limit: 500 } });
          if (!cancelled) setDeviceStatus(buildDeviceStatus(res.data.items, firmware.data.items));
        } catch (versionsError) {
          console.warn('[OTA UPDATES] Could not fetch device versions:', versionsError);
          if (!cancelled) setDeviceStatus([]);
        }
      } catch (err: any) {
        if (cancelled) return;
        console.error('[OTA UPDATES] Error fetching data:', err);
        setFirmwareItems([]);
        setDeviceStatus([]);
        const status = err.response?.status;
        setError(status === 404 || status === 500 ? t('otaUpdates.notInitialised') : err.message || t('otaUpdates.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, refreshTrigger, localRefresh, axios, showInactive, canSeeInactive, page, pageSize, model, type, t]);

  /** The models and types seen on the page, for the filter dropdowns. */
  const models = useMemo(() => Array.from(new Set(firmwareItems.map((fw) => fw.model))).sort(), [firmwareItems]);
  const types = useMemo(() => Array.from(new Set(firmwareItems.map((fw) => fw.type))).sort(), [firmwareItems]);

  const filteredFirmware = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return firmwareItems.filter((fw) => {
      if (active === 'active' && !fw.is_active) return false;
      if (active === 'inactive' && fw.is_active) return false;
      if (!needle) return true;
      return [fw.version, fw.model, fw.changelog ?? '', fw.from_version ?? ''].join(' ').toLowerCase().includes(needle);
    });
  }, [firmwareItems, search, active]);

  const hasFilters = Boolean(search || model || type || active !== 'all');
  const clearFilters = () => {
    setSearch('');
    setModel('');
    setType('');
    setActive('all');
  };

  const figures = useMemo(() => {
    const needingUpdate = deviceStatus.filter((d) => d.needs_update).length;
    const totalDevices = stats?.total_tracked_devices || deviceStatus.length;
    return {
      needingUpdate,
      totalDevices,
      upToDate: totalDevices - needingUpdate,
      coveragePercent: totalDevices > 0 ? Math.round(((totalDevices - needingUpdate) / totalDevices) * 100) : 100,
      inProgress: pendingUpdates.filter((u) => u.status === 'in_progress').length,
      firmwareCount: stats?.total_firmware_versions || firmwareItems.length,
      modelCount: stats?.device_models?.length || 0,
      models: stats?.device_models || [],
    };
  }, [deviceStatus, stats, pendingUpdates, firmwareItems.length]);

  return {
    canSeeInactive, loading, error, firmwareItems, filteredFirmware, deviceStatus, pendingUpdates, figures,
    showInactive, setShowInactive, search, setSearch, model, setModel, models, type, setType, types, active, setActive, hasFilters, clearFilters,
    page, setPage, pageSize, setPageSize, total, totalPages,
    reload: () => setLocalRefresh((n) => n + 1),
  };
};

export type OtaOverviewState = ReturnType<typeof useOtaOverview>;
